import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  message,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography
} from 'antd';
import {
  ArrowLeftOutlined,
  MailOutlined,
  PhoneOutlined,
  UserOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import API from '../../API/API';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;

const CustomerDetails = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCustomerDetails = async () => {
      if (!customerId) return;

      setLoading(true);
      try {
        const response = await API.GetCustomerDetails(customerId);
        if (response?.status) {
          setCustomer(response.data);
          return;
        }

        message.error(response?.message || 'Failed to load customer details');
      } catch (error) {
        console.error('Failed to fetch customer details:', error);
        message.error('Failed to load customer details');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerDetails();
  }, [customerId]);

  const leads = useMemo(() => customer?.leads || [], [customer]);
  const handledByName = useMemo(() => {
    if (!customer?.handledBy) return 'Unassigned';

    const { firstName, lastName, email } = customer.handledBy;
    return `${firstName || ''} ${lastName || ''}`.trim() || email || 'Unassigned';
  }, [customer]);

  const leadColumns = [
    {
      title: 'Lead',
      key: 'lead',
      width: 180,
      render: (_, record) => (
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
            {record.personalInfo?.fullName || customer?.fullName || 'N/A'}
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8' }}>
            ID: {record._id?.slice(-6)?.toUpperCase() || 'N/A'}
          </div>
        </div>
      )
    },
    {
      title: 'Part',
      key: 'part',
      width: 180,
      render: (_, record) => (
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
            {record.vehicleInfo?.part || 'N/A'}
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8' }}>
            {record.vehicleInfo?.year || '—'} {record.vehicleInfo?.make || ''} {record.vehicleInfo?.model || ''}
          </div>
        </div>
      )
    },
    {
      title: 'Status',
      key: 'status',
      width: 150,
      render: (_, record) => (
        <Space direction="vertical" size={4}>
          <Tag color={record.queueLead ? 'gold' : 'blue'}>
            {record.queueLead ? 'Queued' : (record.status || 'Pending')}
          </Tag>
          {record.isExistingCustomer && <Tag color="green">Existing Customer</Tag>}
        </Space>
      )
    },
    {
      title: 'Sales-Agent',
      key: 'agent',
      width: 180,
      render: (_, record) => {
        const agent = record.handledBy;
        const agentName = agent
          ? `${agent.firstName || ''} ${agent.lastName || ''}`.trim() || agent.email || 'Assigned'
          : 'Unassigned';

        return (
          <div>
            <div style={{ fontSize: 13, color: '#0f172a' }}>{agentName}</div>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>{agent?.email || 'No agent email'}</div>
          </div>
        );
      }
    },
    {
      title: 'Added',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (value) => (
        <Tooltip title={value ? dayjs(value).format('MM/DD/YYYY hh:mm A') : 'N/A'}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
              {value ? dayjs(value).format('MM/DD/YY - hh:mm A') : '—'}
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>
              {value ? dayjs(value).fromNow() : ''}
            </div>
          </div>
        </Tooltip>
      )
    },
    {
      title: '',
      key: 'action',
      width: 90,
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => navigate(`/lead-details/${record._id}`, { state: { from: '/customer' } })}
          style={{ padding: 0, fontWeight: 600 }}
        >
          Open Lead
        </Button>
      )
    }
  ];

  return (
    <div style={{ padding: 28, minHeight: '100vh', background: '#f8fafc', fontFamily: 'Sora, sans-serif' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&display=swap');

        .customer-details-table .ant-table-thead > tr > th {
          background: #f8fafc !important;
          font-weight: 700 !important;
          font-size: 11px !important;
          color: #94a3b8 !important;
          letter-spacing: 0.08em !important;
          border-bottom: 1px solid #e2e8f0 !important;
          font-family: 'Sora', sans-serif !important;
        }

        .customer-details-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f1f5f9 !important;
          font-family: 'Sora', sans-serif !important;
        }
      `}</style>

      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/customer')}
          style={{ borderRadius: 12, height: 40 }}
        >
          Back
        </Button>
      </div>

      <div
        style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #0ea5e9 100%)',
          borderRadius: 24,
          padding: '28px 36px',
          marginBottom: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 24,
          flexWrap: 'wrap'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
          <Avatar
            size={58}
            style={{
              background: 'rgba(255,255,255,0.12)',
              border: '1px solid rgba(255,255,255,0.18)'
            }}
            icon={<UserOutlined />}
          />
          <div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#fff', letterSpacing: '-0.02em' }}>
              {customer?.fullName || 'Customer Details'}
            </div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', marginTop: 2 }}>
              Customer ID: {customer?._id || customerId}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Card size="small" style={{ minWidth: 140, borderRadius: 14, background: 'rgba(255,255,255,0.08)', borderColor: 'rgba(255,255,255,0.14)' }}>
            <Text style={{ color: 'rgba(255,255,255,0.65)', fontSize: 11 }}>Total Leads</Text>
            <Title level={3} style={{ margin: '6px 0 0', color: '#fff' }}>{leads.length}</Title>
          </Card>
          <Card size="small" style={{ minWidth: 140, borderRadius: 14, background: 'rgba(255,255,255,0.08)', borderColor: 'rgba(255,255,255,0.14)' }}>
            <Text style={{ color: 'rgba(255,255,255,0.65)', fontSize: 11 }}>Handled By</Text>
            <Title level={5} style={{ margin: '6px 0 0', color: '#fff' }}>{handledByName}</Title>
          </Card>
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '120px 0' }}>
          <Spin size="large" />
        </div>
      ) : !customer ? (
        <Card style={{ borderRadius: 18 }}>
          <Empty description="Customer details not found" />
        </Card>
      ) : (
        <>
          <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
            <Col xs={24} xl={10}>
              <Card title="Customer Information" style={{ borderRadius: 18 }}>
                <Descriptions column={1} size="small" labelStyle={{ fontWeight: 700, color: '#64748b' }}>
                  <Descriptions.Item label="Full Name">{customer.fullName || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Email">
                    {customer.email ? (
                      <Space size={6}>
                        <MailOutlined />
                        <a href={`mailto:${customer.email}`}>{customer.email}</a>
                      </Space>
                    ) : 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Phone">
                    {customer.phoneNumber ? (
                      <Space size={6}>
                        <PhoneOutlined />
                        <a href={`tel:${customer.phoneNumber}`}>{customer.phoneNumber}</a>
                      </Space>
                    ) : 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Handled By">{handledByName}</Descriptions.Item>
                  <Descriptions.Item label="Lead Count">{customer.leadCount ?? leads.length}</Descriptions.Item>
                  <Descriptions.Item label="Created At">
                    {customer.createdAt ? dayjs(customer.createdAt).format('MM/DD/YYYY hh:mm A') : 'N/A'}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>

            <Col xs={24} xl={14}>
              <Card title="Lead Summary" style={{ borderRadius: 18 }}>
                <Alert
                  type="info"
                  showIcon
                  message={`${leads.length} related lead${leads.length === 1 ? '' : 's'} found`}
                  description="This page shows all leads currently linked to the selected customer record."
                />
              </Card>
            </Col>
          </Row>

          <Card
            title="Related Leads"
            style={{ borderRadius: 18 }}
            bodyStyle={{ padding: 0 }}
          >
            <Table
              className="customer-details-table"
              columns={leadColumns}
              dataSource={leads}
              rowKey={(record) => record._id}
              pagination={false}
              locale={{
                emptyText: <Empty description="No related leads found for this customer" />
              }}
              scroll={{ x: 900 }}
            />
          </Card>
        </>
      )}
    </div>
  );
};

export default CustomerDetails;
